import { memo } from 'react'
import type { Column } from '@/types'
import { getColumnKeyLabels } from '@/utils/columnKeys'
import { ColumnKeyIcon } from './ColumnKeyIcon'

interface TableColumnProps {
  col: Column
  tableId: string
}

export const TableColumn = memo(function TableColumn({ col, tableId }: TableColumnProps) {
  const hasDesc = col.desc && col.desc.trim().length > 0
  const keyLabels = getColumnKeyLabels(col)

  return (
    <div
      className="ecol"
      data-tbl={tableId}
      data-col={col.name}
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderBottom: '1px solid var(--col-border)',
        height: 22,
        gap: 3,
        position: 'relative',
        background: 'var(--tbl-body)',
      }}
    >
      {/* Key indicator — fixed width 20px */}
      <span
        style={{
          width: 20,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        title={keyLabels.join(' / ') || undefined}
      >
        <ColumnKeyIcon column={col} />
      </span>

      {/* Column name */}
      <span
        className="cname"
        title={col.name}
        style={{
          flex: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          color: 'var(--tbl-col-name)',
          fontSize: 12,
        }}
      >
        {col.name}
      </span>

      {/* Note icon — only when desc exists */}
      {hasDesc && (
        <span
          title={col.desc}
          style={{
            flexShrink: 0,
            lineHeight: 1,
            opacity: 0.7,
            cursor: 'help',
          }}
        >
          <svg width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden>
            <rect x="0.5" y="0.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="0.9" />
            <line x1="2" y1="3" x2="7" y2="3" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
            <line x1="2" y1="5" x2="6" y2="5" stroke="currentColor" strokeWidth="0.9" strokeLinecap="round" />
          </svg>
        </span>
      )}

      {/* Type */}
      <span
        className="ctype"
        title={col.type}
        style={{
          color: 'var(--text-muted)',
          fontSize: 10,
          flexShrink: 0,
          maxWidth: 76,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {col.type}
      </span>

      {/* NN badge */}
      {col.notNull && (
        <span
          style={{
            color: 'var(--color-danger)',
            fontSize: 9,
            flexShrink: 0,
            fontWeight: 'bold',
            marginLeft: 1,
          }}
        >
          NN
        </span>
      )}

      {/* BL badge */}
      {col.blank && (
        <span
          style={{
            color: '#64748B',
            fontSize: 9,
            flexShrink: 0,
            fontStyle: 'italic',
            marginLeft: 1,
          }}
        >
          BL
        </span>
      )}
    </div>
  )
})
